import type { Static } from "typebox";

import { StringEnum } from "./schema.js";

/**
 * The fixed vocabularies the API speaks. The database has its own copies of
 * several of these; the API checks at compile time that the two agree, so a
 * value added on one side only is a build failure, not a 500.
 */

export const MembershipRole = StringEnum(["owner", "admin", "member"]);
export type MembershipRole = Static<typeof MembershipRole>;

/**
 * What kind of work a fleet does, asked once at setup. Short on purpose:
 * it tailors suggestions, and "other" is always a fine answer.
 */
export const organizationIndustries = [
  "pest_control",
  "hvac_plumbing_electrical",
  "landscaping",
  "cleaning",
  "construction",
  "delivery",
  "field_service",
  "other",
] as const;
export const OrganizationIndustry = StringEnum(organizationIndustries);
export type OrganizationIndustry = Static<typeof OrganizationIndustry>;

export const VehicleLifecycleState = StringEnum([
  "active",
  "out_of_service",
  "sold",
  "archived",
]);
export type VehicleLifecycleState = Static<typeof VehicleLifecycleState>;

export const MaintenanceDueStateName = StringEnum([
  "clear",
  "upcoming",
  "due",
  "overdue",
  "paused",
  "needs_baseline",
  "unknown_mileage",
]);
export type MaintenanceDueStateName = Static<typeof MaintenanceDueStateName>;

export const MaintenanceRuleState = StringEnum(["active", "paused"]);
export type MaintenanceRuleState = Static<typeof MaintenanceRuleState>;

/** Where a rule's baseline came from; "unknown" means it needs setting up. */
export const MaintenanceBaselineSource = StringEnum([
  "service_record",
  "historical_entry",
  "rule_start",
  "unknown",
]);
export type MaintenanceBaselineSource = Static<
  typeof MaintenanceBaselineSource
>;

/** Which half of a rule has nothing to count from. */
export const MaintenanceBaselineGap = StringEnum(["date", "mileage"]);
export type MaintenanceBaselineGap = Static<typeof MaintenanceBaselineGap>;

export const MeterSource = StringEnum([
  "vehicle_creation",
  "manual",
  "driver_check_in",
  "service_record",
  "correction",
]);
export type MeterSource = Static<typeof MeterSource>;

export const IssueCategory = StringEnum([
  "body",
  "brakes",
  "electrical",
  "engine",
  "fluids",
  "lights",
  "tires",
  "other",
]);
export type IssueCategory = Static<typeof IssueCategory>;

export const IssueSeverity = StringEnum(["low", "medium", "high", "critical"]);
export type IssueSeverity = Static<typeof IssueSeverity>;

export const IssueStatus = StringEnum([
  "open",
  "acknowledged",
  "scheduled",
  "resolved",
  "dismissed",
]);
export type IssueStatus = Static<typeof IssueStatus>;

/** What a person does to an issue; the API maps each to the status it lands in. */
export const IssueAction = StringEnum([
  "acknowledge",
  "dismiss",
  "reopen",
  "resolve",
  "schedule",
]);
export type IssueAction = Static<typeof IssueAction>;

export const IssueListSort = StringEnum(["newest", "queue"]);
export type IssueListSort = Static<typeof IssueListSort>;

export const IssueReporterType = StringEnum(["driver", "user"]);
export type IssueReporterType = Static<typeof IssueReporterType>;

/** Who made a change, as the issue timeline records it. */
export const ChangeActorType = StringEnum([
  "user",
  "driver_token",
  "system_job",
  "webhook",
]);
export type ChangeActorType = Static<typeof ChangeActorType>;

export const ServiceRecordState = StringEnum(["completed", "corrected"]);
export type ServiceRecordState = Static<typeof ServiceRecordState>;

export const AttachmentCategory = StringEnum([
  "receipt",
  "issue_photo",
  "odometer_photo",
  "document",
]);
export type AttachmentCategory = Static<typeof AttachmentCategory>;

export const FileLifecycleState = StringEnum(["active", "deleted"]);
export type FileLifecycleState = Static<typeof FileLifecycleState>;

export const DriverLinkScope = StringEnum(["issue:write", "meter:write"]);
export type DriverLinkScope = Static<typeof DriverLinkScope>;

export const InvitationStatus = StringEnum([
  "accepted",
  "expired",
  "not_found",
  "pending",
  "revoked",
]);
export type InvitationStatus = Static<typeof InvitationStatus>;

export const AuditEntityType = StringEnum([
  "driver",
  "driver_access_token",
  "invitation",
  "issue",
  "maintenance_rule",
  "maintenance_rule_version",
  "maintenance_template",
  "membership",
  "meter_entry",
  "notification_preference",
  "organization",
  "user",
  "vehicle",
  "vehicle_assignment",
  "vehicle_document",
  "vehicle_recall",
  "vendor",
  "service_record",
]);
export type AuditEntityType = Static<typeof AuditEntityType>;

export const AuditActorKind = StringEnum(["driver", "member"]);
export type AuditActorKind = Static<typeof AuditActorKind>;
